import { intlLocale, t } from './i18n';

/** Formatters for the current interface language, rebuilt when it changes. */
const formatterCache = new Map<string, Intl.DateTimeFormat>();
function dateFormat(name: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${intlLocale()}|${name}`;
  let format = formatterCache.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(intlLocale(), options);
    formatterCache.set(key, format);
  }
  return format;
}

const time = () => dateFormat('time', { hour: '2-digit', minute: '2-digit' });
const dayMonth = () => dateFormat('dayMonth', { day: 'numeric', month: 'short' });
const full = () => dateFormat('full', { weekday: 'long', day: 'numeric', month: 'long' });
const fullWithYear = () =>
  dateFormat('fullWithYear', { day: 'numeric', month: 'long', year: 'numeric' });
const shortWithYear = () =>
  dateFormat('shortWithYear', { day: 'numeric', month: 'short', year: 'numeric' });

/** Number in the interface language: "1.234" / "1,234". */
export const formatNumber = (n: number, options?: Intl.NumberFormatOptions) =>
  n.toLocaleString(intlLocale(), options);

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const DAY_MS = 86_400_000;

/** Whole days from today to `date` (negative in the past). */
export function daysFromToday(date: Date, now = new Date()): number {
  return Math.round((startOfDay(date).getTime() - startOfDay(now).getTime()) / DAY_MS);
}

export const formatTime = (iso: string) => time().format(new Date(iso));

/** "Hoy", "Ayer" or a long date, used for message day separators. */
export function formatDay(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const diff = daysFromToday(date, now);
  if (diff === 0) return t('Hoy');
  if (diff === -1) return t('Ayer');
  const text =
    date.getFullYear() === now.getFullYear() ? full().format(date) : fullWithYear().format(date);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Relative time for notifications: "ahora", "hace 5 min", "hace 3 h", "12 sept". */
export function formatRelative(iso: string, now = new Date()): string {
  const seconds = Math.round((now.getTime() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return t('ahora');
  if (seconds < 3600) return t('hace {{n}} min', { n: Math.floor(seconds / 60) });
  if (seconds < 86_400) return t('hace {{n}} h', { n: Math.floor(seconds / 3600) });
  return dayMonth().format(new Date(iso));
}

/** Parses a `YYYY-MM-DD` due date as a local date (not UTC midnight). */
export function parseDueDate(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export type DueState = 'overdue' | 'today' | 'soon' | 'later';

export function dueState(value: string, now = new Date()): DueState {
  const diff = daysFromToday(parseDueDate(value), now);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 2) return 'soon';
  return 'later';
}

export function formatDueDate(value: string, now = new Date()): string {
  const diff = daysFromToday(parseDueDate(value), now);
  if (diff === 0) return t('Hoy');
  if (diff === 1) return t('Mañana');
  if (diff === -1) return t('Ayer');
  return dayMonth().format(parseDueDate(value));
}

/** Local calendar date as `YYYY-MM-DD`, `offsetDays` from today. */
export function isoDateFromToday(offsetDays: number, now = new Date()): string {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offsetDays);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const formatLongDate = (iso: string) => fullWithYear().format(new Date(iso));

/** "24 sept 2026": a date with its year where space is tight. */
export const formatShortDate = (iso: string) => shortWithYear().format(new Date(iso));

/** Human file size: "820 B", "12 KB", "3,4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} MB`;
}
